import React, { useState } from "react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
} from "../../../components/ui/dialog";
import { useLanguage } from "../../../contexts/LanguageContext";
import { settingsLabels } from "../labels";
import { Plus, TrendingUp, Loader2 } from "lucide-react";
import { api } from "../../../api";
import { toast } from "sonner";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function NewSpecialtyDrawer({ open, onOpenChange, onSuccess }: Props) {
  const { lang } = useLanguage();
  const t = settingsLabels[lang].specialties.newPage;
  
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const save = async () => {
    if (!name.trim()) {
      toast.error(t.requiredError);
      return;
    }

    setLoading(true);
    const res = await api.createCompanySpecialty({ name: name.trim() });
    setLoading(false);

    if (res.ok) {
      toast.success(t.success);
      setName("");
      onOpenChange(false);
      onSuccess();
    } else {
      toast.error(t.requestError);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md border-0 rounded-[2.5rem] shadow-2xl p-0 overflow-hidden bg-white gap-0">
        <DialogHeader className="px-8 py-8 bg-neutral-50/50 border-b border-neutral-100">
          <div className="w-14 h-14 rounded-[2rem] bg-emerald-50 flex items-center justify-center text-emerald-600 mb-4 animate-in zoom-in-50 duration-500">
            <TrendingUp className="w-7 h-7" />
          </div>
          <DialogTitle className="text-xl font-bold text-neutral-800 tracking-tight">
            {t.title}
          </DialogTitle>
          <DialogDescription className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest mt-1">
            {t.description}
          </DialogDescription>
        </DialogHeader>

        <div className="p-8 space-y-6">
          <div className="space-y-2">
            <Label htmlFor="spec-name" className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1 block">
              {t.fields.name}
            </Label>
            <div className="relative group">
              <TrendingUp className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 transition-colors group-focus-within:text-emerald-600" />
              <Input
                id="spec-name"
                placeholder="Ex: Fisioterapia, Nutrição..."
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="pl-11 h-14 rounded-2xl border-neutral-100 bg-neutral-50/30 focus-visible:ring-emerald-500/20 text-sm font-medium focus:bg-white transition-all"
                onKeyDown={(e) => e.key === "Enter" && save()}
              />
            </div>
          </div>

          <div className="pt-4 space-y-3">
            <Button
              onClick={save}
              disabled={loading}
              className="w-full h-14 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-2xl transition-all active:scale-[0.98]"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Plus className="w-5 h-5 mr-2" />
                  {t.save}
                </>
              )}
            </Button>
            <Button
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="w-full h-14 text-neutral-400 font-bold hover:bg-neutral-100 rounded-2xl transition-all"
            >
              {t.cancel}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
